import { useCallback } from 'react'
import { useLocation, useSearch } from 'wouter'
import { useT } from '~/i18n/index.tsx'
import { useSuperadmin } from '~/lib/superadmin.ts'

export const SORTS = ['symbol', 'name', 'documents', 'quality'] as const
export type Sort = (typeof SORTS)[number]

const isSort = (s: string | null): s is Sort => !!s && (SORTS as readonly string[]).includes(s)

/** The sort is a URL parameter like everything else, so a link carries it. */
export function useSortParam() {
  const { t } = useT()
  const superadmin = useSuperadmin()
  const search = useSearch()
  const [location, navigate] = useLocation()

  const raw = new URLSearchParams(search).get('sort')
  // `?sort=quality` in a saved link must not resurrect the control, or the
  // one thing this mode hides comes back through a bookmark.
  const named: Sort = isSort(raw) ? raw : 'symbol'
  const sort: Sort = named === 'quality' && !superadmin ? 'symbol' : named

  const setSort = useCallback(
    (next: Sort) => {
      const sp = new URLSearchParams(search)
      if (next === 'symbol') sp.delete('sort')
      else sp.set('sort', next)
      const s = sp.toString()
      navigate(`${location}${s ? `?${s}` : ''}`, { replace: true })
    },
    [search, location, navigate],
  )

  const SORT_OPTIONS = [
    { id: 'symbol' as const, label: t('results.sort.symbol') },
    { id: 'name' as const, label: t('results.sort.name') },
    { id: 'documents' as const, label: t('results.sort.documents') },
    ...(superadmin ? [{ id: 'quality' as const, label: t('results.sort.quality') }] : []),
  ]

  return { sort, setSort, SORT_OPTIONS }
}
